import { Box, styled, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  LayoutDropdown,
  NavigationMenu,
  regionActions,
  sectionSelectors,
  useIsIdle,
  useSelectRegionsActive,
  useSelectWidgetInstanceById,
  useSelectWidgetInstanceEntities,
  widgetInstanceActions,
  type SettingsConfig,
  type WidgetId,
  useAppDispatch,
  useSelectLayoutManagerMode,
} from '#features';
import {
  ELayoutManagerMode,
  ESettingsGroup,
  EWidgetRegion,
  EWidgetSection,
  SettingControl,
  useIsPortraitMode,
  type ControlsProps,
} from '#shared';

import { WidgetSettingsPopover } from './WidgetSettingsPopover';
import { widgetClasses } from './WidgetStyled';

const PREFIX = 'TSMED-Toolbar';
export const toolbarClasses = {
  root: `${PREFIX}-root`,
  row: `${PREFIX}-row`,
  dragger: `${PREFIX}-dragger`,
  draggerIcon: `${PREFIX}-draggerIcon`,
};

const Root = styled(Box, {
  shouldForwardProp: p =>
    p !== 'isIdle' && p !== 'forceVisible' && p !== 'isSettingsMode',
})<{ isIdle: boolean; forceVisible: boolean; isSettingsMode: boolean }>(
  ({ theme: { spacing, palette }, isIdle, forceVisible, isSettingsMode }) => ({
    [`&.${toolbarClasses.root}`]: {
      position: 'absolute',
      top: spacing(1),
      right: spacing(1),
      zIndex: 100,
      // In settings mode: always visible, ignore hover/idle
      // In RENDER mode: hidden by default, shown on hover (unless idle)
      opacity: isSettingsMode ? 1 : 0,
      pointerEvents: isSettingsMode ? 'auto' : 'none',
      transition: 'opacity 150ms ease-in-out',
      // Hover behavior only applies in RENDER mode
      ...(!isSettingsMode && {
        [`.${widgetClasses.root}:hover &`]: {
          opacity: isIdle ? 0 : 1,
          pointerEvents: isIdle ? 'none' : 'auto',
        },
      }),
      // Force visible when any popover is open (only in RENDER mode)
      ...(forceVisible && !isIdle && !isSettingsMode
        ? { opacity: 1, pointerEvents: 'auto' }
        : null),
    },
    [`.${toolbarClasses.dragger}`]: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      boxSizing: 'border-box',
      cursor: 'grab',
      verticalAlign: 'middle',
      background: 'rgba(29, 42, 55, 0.50)',
      flex: '0 0 auto',
      fontSize: '1.5rem',
      padding: '8px',
      transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      backgroundColor: '#1D2A37',
      border: '2px solid #364B5F',
      borderRadius: '12px',
      color: '#fff',
      width: '40px',
      height: '40px',
      overflow: 'hidden',

      '& svg': {
        fill: palette.common.white,
      },

      '&:hover': {
        backgroundColor: palette.mapBlue[800],
      },

      '&.Mui-disabled': {
        opacity: 0.3,
        color: palette.common.white,
      },
    },
    [`.${toolbarClasses.row}`]: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing(1),
    },
  }),
);

/** Controls the popover can render (strictly typed). */
export type ControlMap = Record<string, ControlsProps>;
type UnknownSettings = Record<string, unknown>;

interface WidgetToolbarProps {
  /** Widget instance this toolbar controls */
  instanceId: WidgetId;
  /** Pure controls schema (no legacy keying) */
  config: ControlMap;
  /** Optional title */
  title?: string;
}

export const WidgetToolbar = ({
  instanceId,
  config,
  title = 'Widget Settings',
}: WidgetToolbarProps) => {
  const dispatch = useAppDispatch();
  const isIdle = useIsIdle();
  const location = useLocation();
  const layoutManagerMode = useSelectLayoutManagerMode();

  // In settings mode (VIEW or EDIT), toolbar should be always visible for touch devices
  const isSettingsMode =
    layoutManagerMode === ELayoutManagerMode.VIEW ||
    layoutManagerMode === ELayoutManagerMode.EDIT;

  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  const isPortraitMode = useIsPortraitMode();

  const [isSettingsOpenInternal, setIsSettingsOpen] = useState(false);
  const [isLayoutOpenInternal, setIsLayoutOpen] = useState(false);
  const [isNavOpenInternal, setIsNavOpen] = useState(false);
  const hasRun = useRef(false);

  // Instance + settings from widgetInstances slice
  const inst = useSelectWidgetInstanceById(instanceId);
  const widgetInstances = useSelectWidgetInstanceEntities();
  const activeRegions = useSelectRegionsActive();
  const centerSection = sectionSelectors.useSelectSectionById(
    EWidgetSection.center,
  );
  const currentSettings = useMemo<Record<string, ControlsProps['value']>>(
    () => (inst?.settings ?? {}) as Record<string, ControlsProps['value']>,
    [inst?.settings],
  );

  // Show full toolbar (settings + layout + navigation) when:
  // - There's NO geo-map actually placed in center section's blockOrder, OR
  // - The center region is not active (not visible)
  // When geo-map is in center AND center region is visible, show only settings button
  const showFullToolbar = useMemo(() => {
    // Check if Center region is active (enabled and not hidden)
    const isCenterRegionActive = activeRegions.some(
      region => region.id === EWidgetRegion.Center,
    );

    // Check if there's a geo-map actually placed in the center section's blockOrder
    const centerBlockIds = centerSection?.blockOrder ?? [];
    const hasGeoMapInCenter = centerBlockIds.some(blockId => {
      const widget = widgetInstances[blockId];
      return widget?.type === 'geo-map';
    });

    // Show full toolbar if center region is not active OR there's no geo-map in center
    return !isCenterRegionActive || !hasGeoMapInCenter;
  }, [widgetInstances, activeRegions, centerSection]);

  // Auto-collapse on idle - derive state instead of syncing in effect
  const isSettingsOpen = isSettingsOpenInternal && !isIdle;
  const isLayoutOpen = isLayoutOpenInternal && !isIdle;
  const isNavOpen = isNavOpenInternal && !isIdle;

  // Auto switch to Center-only in one-column mode (widgetManager controls it)
  useEffect(() => {
    const oneCol = isBelowMd || isPortraitMode;
    if (!hasRun.current) {
      hasRun.current = true;

      if (oneCol) {
        dispatch(
          regionActions.regionShow({
            regionId: EWidgetRegion.Center,
            only: true,
          }),
        );
      } else {
        dispatch(
          regionActions.regionHide({
            regionId: [],
            only: true,
          }),
        );
      }
    }
  }, [isBelowMd, isPortraitMode, dispatch]);

  // Persist a setting change to the instance (deep-merge in slice)
  const handleSettingChange = useCallback(
    (name: string, value: ControlsProps['value']) => {
      if (inst) {
        dispatch(
          widgetInstanceActions.setWidgetInstance({
            id: instanceId,
            title: inst.title,
            type: inst.type,
            sectionId: inst.sectionId,
            settings: { [name]: value },
          }),
        );
      }
    },
    [inst, dispatch, instanceId],
  );

  const anyOpen = isSettingsOpen || isLayoutOpen || isNavOpen;

  // Popover expects a SettingsConfig shape; we provide a typed shim
  const settingsConfig = useMemo<SettingsConfig<UnknownSettings>>(
    () => ({
      group: ESettingsGroup.WIDGETS,
      name: inst?.type ?? 'Widget',
      config: config as Record<string, ControlsProps>,
    }),
    [config, inst?.type],
  );

  return (
    <Root
      className={toolbarClasses.root}
      isIdle={isIdle}
      forceVisible={anyOpen}
      isSettingsMode={isSettingsMode}
      // Prevent all toolbar interactions from propagating to parent (drag handlers)
      onPointerDown={e => e.stopPropagation()}
      onPointerUp={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onMouseUp={e => e.stopPropagation()}
      onTouchStart={e => e.stopPropagation()}
      onTouchEnd={e => e.stopPropagation()}
    >
      <Box className={toolbarClasses.row}>
        {/* Always render settings popover - it contains settings page link even if no widget-specific settings */}
        <WidgetSettingsPopover
          title={title}
          settings={settingsConfig}
          currentSettings={currentSettings}
          onSettingChange={handleSettingChange}
          settingControlComponent={SettingControl}
          isIdle={isIdle}
          onOpenChange={setIsSettingsOpen}
        />
        {/* <WidgetDragger instanceId={instanceId} /> */}
        {/* Layout + Navigation: hide in VIEW/EDIT mode, and when geo-map is in center */}
        {!isSettingsMode && showFullToolbar && (
          <>
            <LayoutDropdown onOpenChange={setIsLayoutOpen} />
            <NavigationMenu
              value={location.pathname}
              onOpenChange={setIsNavOpen}
            />
          </>
        )}
      </Box>
    </Root>
  );
};
